import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { NotificationCategory, PracticeState } from './commandCenterApi';

/** The modules a practice can be running. A card shows only the ones it has. */
export type ModuleKey =
  | 'visibility'
  | 'reviews'
  | 'ads'
  | 'calls'
  | 'safety'
  | 'insurance'
  | 'strategy';

/**
 * `na` is a real answer, not a failure — a practice that skipped ads on
 * purpose reads "not in scope" rather than scoring zero.
 */
export type ReadinessState = 'done' | 'blocked' | 'pending' | 'na';

export interface ReadinessStep {
  key: string;
  label: string;
  state: ReadinessState;
  note: string | null;
}

export interface ModuleMetric {
  headline: string;
  unit: string;
  delta: string | null;
  read: string;
}

export interface NeedsYouItem {
  id: string;
  code: string;
  category: NotificationCategory;
  title: string;
  description: string;
  actionLabel: string | null;
  deepLink: string | null;
  /** When it started waiting. Rendered as an age, never a countdown. */
  waitingSince: string;
  urgent: boolean;
}

export interface LogEntry {
  id: string;
  at: string;
  actor: string;
  message: string;
  awaiting: 'you' | 'them' | null;
}

/** Hired agents per role, across the practice's locations. One entry per hire-able role. */
export interface SupervisionAgent {
  roleKey: string;
  personaName: string;
  hiredLocations: number;
}

export interface SyncFailure {
  provider: string;
  locationName: string | null;
  message: string;
}

export interface SupervisionCard {
  id: string;
  name: string;
  vertical: string;
  specialty: string | null;
  status: string;
  state: PracticeState;
  locationsCount: number;
  snoozedAt: string | null;
  /** Google/Meta ads connections whose last sync failed. Empty when healthy. Absent from older backends. */
  syncFailed?: SyncFailure[];
  modules: ModuleKey[];
  /** The same score the practice header shows. Null when nothing is measurable yet. */
  healthScore: number | null;
  agents: SupervisionAgent[];
  /** When the platform last did something for this practice: an agent run, audit, sync or review request. */
  lastSystemActionAt: string | null;
  /** When anyone on the practice's own team last signed in. Null means nobody has. */
  clientLastLoginAt: string | null;
  needsYou: NeedsYouItem[];
  waitingOnClient: NeedsYouItem[];
  readiness: ReadinessStep[];
  metrics: Partial<Record<ModuleKey, ModuleMetric>>;
  log: LogEntry[];
  story: string[];
}

export interface SupervisionSummary {
  practiceCount: number;
  needsYouCount: number;
  waitingOnClientCount: number;
  actionsLastDay: number;
  snoozedCount: number;
}

export const supervisionApi = {
  getSupervision: async (): Promise<{
    cards: SupervisionCard[];
    summary: SupervisionSummary;
  }> => {
    const { data } =
      await apiClient.get<ApiResponse<{ cards: SupervisionCard[]; summary: SupervisionSummary }>>(
        '/agency/supervision',
      );
    return data.data;
  },
};
